import classNames from 'classnames';
import { useField, useFormikContext } from 'formik';
import { useCallback } from 'react';
import { Button } from 'yoso';
import {
  Adventure,
  useAdventure,
  useAdventureNextPrice,
} from '~/components/Adventure/Adventure.hooks';
import { JoinAdventureValues } from '~/components/Adventure/Form/AdventureForm.hooks';
import { SpinNumberInput } from '~/components/Form/Number/Spin/SpinNumberInput';
import { T } from '~/components/I18n';
import { AutoSizeText } from '~/components/Text/AutoSize/AutoSizeText';
import { format } from '~/components/Util/number';
import styles from './AdventureCountField.module.scss';

export type AdventureCountFieldProps = {
  name?: string;
  className?: string;
  autoFocus?: boolean;
} & Pick<Adventure, 'id'>;
export const AdventureCountField = ({
  id,
  name = 'count',
  className,
  autoFocus,
}: AdventureCountFieldProps) => {
  const {
    values: { balance },
  } = useFormikContext<JoinAdventureValues>();
  const [field, meta, { setValue }] = useField({
    type: 'number',
    name,
  });
  const { data: adventure } = useAdventure(id);
  const participatedCount = adventure?.participation.currentCount || 0;
  const nextPrice = useAdventureNextPrice(id);

  const maxCount = adventure?.participation.maxCount || 12;

  const remainingCount = Math.max(maxCount - participatedCount, 0);
  const availableCount = Math.min(Math.floor(balance / nextPrice), remainingCount);

  const onChange = useCallback(
    (e) => {
      setValue(Number(e.currentTarget.value));
    },
    [setValue]
  );

  const disableMaxButton =
    balance < nextPrice || field.value === availableCount || remainingCount === 0;

  const onMaxButtonClick = useCallback(() => {
    setValue(availableCount);
  }, [setValue, availableCount]);

  return (
    <div className={classNames(styles.root, className, { [styles.error]: meta.error })}>
      <div className={styles.body}>
        <span className={styles.don}>
          <AutoSizeText>{format(field.value * nextPrice)}DON</AutoSizeText>
        </span>
        <SpinNumberInput
          className={styles.input}
          min={1}
          max={availableCount}
          autoFocus={autoFocus}
          mask="9999999"
          {...field}
          onChange={onChange}
        />
      </div>
      <div className={styles.footer}>
        <Button
          className={styles.maxButton}
          variant="text"
          disabled={disableMaxButton}
          onClick={onMaxButtonClick}>
          <T name="citizen_adventure_participatenum_max" />
        </Button>
        <p className={styles.message}>
          {(() => {
            switch (meta.error) {
              case 'MAX_COUNT':
                return (
                  <>
                    <T name="citizen_adventure_participatenum_alert_desc" />:{' '}
                    {format(remainingCount)}
                  </>
                );
              default:
                return (
                  <>
                    <T name="citizen_adventure_participatenum_availablelup" options={['DON']} />:{' '}
                    {format(balance)}
                  </>
                );
            }
          })()}
        </p>
      </div>
    </div>
  );
};
